import React, { useEffect } from "react";
import cx from "classnames";
import { Switch, Route, Redirect } from "react-router-dom";

// Perfect scrollbar
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";

// Core layout components
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import CookieBanner from "components/CookieBanner";

// Assets
import sidebarImage from "assets/img/sidebar-2.jpg";
import logoWhite from "assets/img/logo-white.png";

// Context Providers
import { AuthProvider, useAuth } from "../../src/context/AuthContext";
import { MutualLikesProvider } from "../../src/context/MutualLikesContext";
import WebSocketProvider from "../../src/context/WebSocketContext";
import { ChatProvider } from "../../src/context/ChatContext";
import CallProvider from "../../src/context/CallContext";
import { SearchProvider } from "../../src/context/SearchContext";
import { UnreadProvider } from "../../src/context/UnreadContext";

// ✅ Consent
import { ConsentProvider, useConsent } from "../../src/consent/ConsentContext";

// Route Guards
import CompleteRoute from "../../src/context/CompleteRoute";
import PrivateRoute from "../../src/context/PrivateRoute";
import VerifiedRoute from "../../src/context/VerifiedRoute";

// Global handlers/modals
import GlobalCallHandler from "../views/Components/Chat/GlobalCallHandler";
import GlobalChatModal from "../views/Components/Chat/GlobalChatModal";

// Profile & banners
import MyProfile from "views/Dashboard/MyProfile.js";
import RenewSubscription from "views/Dashboard/RenewSubscription.js";
import AppConstants from "constants/appConstants";
import ProfileIncompleteBanner from "views/Dashboard/ProfileIncompleteBanner.js";

// Routes config
import routes from "routes.js";

// Toast
import { Toaster } from "react-hot-toast";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/layouts/adminStyle.js";

// 🔥 Push
import { registerForPush } from "pushNotifications";
import FcmAPI from "api_calls/FcmAPI";

const useStyles = makeStyles(styles);

/* ---------------------------------------------------
   🔔 PUSH BOOTSTRAP (CONSENT AWARE)
--------------------------------------------------- */

function PushBootstrap() {
  const { user } = useAuth();
  const { consent, hasConsented } = useConsent();

  useEffect(() => {
    if (!user) return;
    if (!hasConsented) return;

    const allowed = consent?.preferences || consent?.marketing;

    if (!allowed) {
      console.log("🚫 Push blocked by consent");
      return;
    }

    if (Notification.permission === "granted") {
      console.log("🔁 [PUSH] Syncing token...");

      registerForPush().then((token) => {
        if (token) {
          FcmAPI.updateFcmToken(token);
        }
      });
    }
  }, [user, consent, hasConsented]);

  return null;
}

/* ---------------------------------------------------
   MAIN DASHBOARD LAYOUT
--------------------------------------------------- */

export default function Dashboard(props) {
  const { ...rest } = props;

  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [miniActive, setMiniActive] = React.useState(true);
  const [image, setImage] = React.useState(sidebarImage);
  const [color, setColor] = React.useState("blue");
  const [bgColor, setBgColor] = React.useState("black");
  const [logo, setLogo] = React.useState(logoWhite);

  const classes = useStyles();

  const mainPanelClasses =
    classes.mainPanel +
    " " +
    cx({
      [classes.mainPanelSidebarMini]: miniActive,
      [classes.mainPanelWithPerfectScrollbar]:
        navigator.platform.indexOf("Win") > -1,
    });

  const mainPanel = React.useRef(null);

  /* -----------------------------
     PWA INSTALL PROMPT
  ------------------------------*/

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  /* -----------------------------
     SCROLLBAR
  ------------------------------*/

  useEffect(() => {
    let scrollbarInstance = null;

    if (navigator.platform.indexOf("Win") > -1 && mainPanel.current) {
      scrollbarInstance = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });

      document.body.style.overflow = "hidden";
    }

    return () => {
      if (scrollbarInstance) {
        scrollbarInstance.destroy();
      }
    };
  }, []);

  /* -----------------------------
     HELPERS
  ------------------------------*/

  const sidebarMinimize = () => setMiniActive(!miniActive);

  const getRoute = () => window.location.pathname !== "/admin/full-screen-maps";

  const getRoutes = (routes) =>
    routes.map((prop, key) => {
      if (prop.collapse) return getRoutes(prop.views);

      if (prop.layout === "/singles" && prop.component !== MyProfile) {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      }
      return null;
    });

  /* ---------------------------------------------------
     RENDER
  --------------------------------------------------- */

  return (
    <ConsentProvider>
      <AuthProvider>
        <UnreadProvider>
          <WebSocketProvider>
            <MutualLikesProvider>
              <ChatProvider>
                <CallProvider>
                  <SearchProvider>
                    {/* Toasts */}
                    <Toaster position="top-center" />

                    {/* Push (Consent Aware) */}
                    <PushBootstrap />

                    {/* 👉 Cookie Banner mounts here later */}
                    <CookieBanner />

                    {/* Global Modals */}
                    <GlobalCallHandler />
                    <GlobalChatModal />

                    <div className={classes.wrapper}>
                      <Sidebar
                        routes={routes}
                        logoText={"Beramour"}
                        logo={logo}
                        image={image}
                        handleDrawerToggle={() => setMobileOpen((p) => !p)}
                        open={mobileOpen}
                        color={color}
                        bgColor="black"
                        miniActive={miniActive}
                        deferredPrompt={deferredPrompt}
                        setDeferredPrompt={setDeferredPrompt}
                        {...rest}
                      />

                      <div className={mainPanelClasses} ref={mainPanel}>
                        <AdminNavbar
                          sidebarMinimize={sidebarMinimize}
                          miniActive={miniActive}
                          brandText="Beramour"
                          handleDrawerToggle={() => setMobileOpen((p) => !p)}
                          {...rest}
                        />

                        <ProfileIncompleteBanner />

                        {getRoute() ? (
                          <div className={classes.content}>
                            <div className={classes.container}>
                              <Switch>
                                <PrivateRoute>
                                  <VerifiedRoute>
                                    <Route
                                      path="/singles/my-profile/:userId/:section?"
                                      component={MyProfile}
                                    />
                                    <Route
                                      path={AppConstants.RENEW_SUBSCRIPTION_PATH}
                                      component={RenewSubscription}
                                    />
                                    <CompleteRoute>
                                      {getRoutes(routes)}
                                    </CompleteRoute>
                                  </VerifiedRoute>
                                </PrivateRoute>
                              </Switch>
                            </div>
                          </div>
                        ) : (
                          <div className={classes.map}>
                            <Switch>
                              {getRoutes(routes)}
                              <Redirect
                                from="/singles"
                                to="/singles/dashboard"
                              />
                            </Switch>
                          </div>
                        )}

                        {getRoute() ? <Footer fluid /> : null}
                      </div>
                    </div>
                  </SearchProvider>
                </CallProvider>
              </ChatProvider>
            </MutualLikesProvider>
          </WebSocketProvider>
        </UnreadProvider>
      </AuthProvider>
    </ConsentProvider>
  );
}
